<script lang="ts">
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";

  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { params, page } from "@roxi/routify";
  import { afterUpdate } from "svelte";

  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import ProductTypesStore from "@shared/svelte/Stores/ProductTypesStore.js";

  import type { Category, Subcategory, ProductType } from "@shared/types/ProductLine";

  export let subcategoryIdProp: number = null;
  export let overridePageTitle: boolean = false;

  let showLargeTextFinalBreadcrumb = $page.path !== "/product_listing" ? false : true;

  let customText: string | null = "";
  let largeBreadcrumbText: string | null = null;
  let pageTitle: string = null;

  let category: Category = null;
  let subcategory: Subcategory = null;
  let productType: ProductType = null;

  const setup = async () => {
    if (distributorName) {
      showLargeTextFinalBreadcrumb = false;
      return;
    } else if (search) {
      largeBreadcrumbText = searchText;
      return;
    } else if (subcategoryId || categoryId || productTypeId) {
      return setupCategorizationBreadcrumbs();
    } else {
      const res = await HttpPromise({
        method: "GET",
        url: "/api/breadcrumb",
        data: $params,
      });

      const breadcrumbName = res?.name;
      if (breadcrumbName) {
        customText = breadcrumbName;
        largeBreadcrumbText = breadcrumbName;
      } else {
        customText = "All Products";
        largeBreadcrumbText = customText;
      }
    }
  };

  const setupCategorizationBreadcrumbs = () => {
    if (subcategoryId) {
      subcategory = categorization.subcategories.find((type: any) => type.id == subcategoryId);
      if (subcategory) {
        categoryId = categorization.categories.find(
          (type: any) => type.id == subcategory.category_id,
        ).id;
      }
    }

    if (categoryId) {
      category = categorization.categories.find((type: any) => type.id == categoryId);
      if (category) {
        productTypeId = categorization.types.find(
          (type: any) => type.id == category.product_type_id,
        ).id;
      }
    }

    if (productTypeId) {
      productType = categorization.types.find((type: any) => type.id == productTypeId);
    }

    largeBreadcrumbText = subcategory?.name || category?.name || productType?.name;
  };

  afterUpdate(() => {
    setup();
  });

  $: categorization = $ProductTypesStore as any;
  $: categoryId = $params.category;
  $: distributorName = $DistributorsStore.find((d)=> d.id == $params.distributors?.[0])?.name;
  $: productTypeId = $params.type;
  $: subcategoryId = $params.subcategory || subcategoryIdProp;
  $: search = $params.search;
  $: searchText = search?.length <= 41 ? search : search?.slice(0, 41).concat("...");
  $: searchBreadcrumb = search ? `Search Results for "${searchText}"` : null;

  $: pageTitle =
    customText ||
    searchBreadcrumb ||
    distributorName ||
    subcategory?.name ||
    category?.name ||
    productType?.name;
</script>

<div id="breadcrumb-navigation">
  {#if overridePageTitle}
    <PageTitle title={`${pageTitle} | Provi`} />
  {/if}
  <div
    class="fizz-container"
    class:fizz-stack-8={showLargeTextFinalBreadcrumb}
    data-testid="breadcrumb-navigation"
  >
    <ol class="fizz-breadcrumbs">
      <li>
        <a href="/" data-testid="home-breadcrumb" class="fizz-text-subdued">Home</a>
      </li>
      {#if distributorName}
        <li data-testid="all-distributors-breadcrumb">
          <a href="/distributors"> All Distributors </a>
        </li>
        <li data-testid="distributor-breadcrumb">{distributorName}</li>
      {/if}

      {#if search}
        <li data-testid="search-breadcrumb">
          {searchBreadcrumb}
        </li>
      {/if}

      {#if customText}
        <li data-testid="custom-text-breadcrumb">
          {customText}
        </li>
      {/if}

      {#if productType}
        <li data-testid="product-type-breadcrumb">
          <a href="/product_listing?type={productTypeId}">
            {productType.name}
          </a>
        </li>
      {/if}

      {#if category}
        <li data-testid="category-breadcrumb">
          <a href="/product_listing?category={categoryId}">
            {category.name}
          </a>
        </li>
      {/if}

      {#if subcategory}
        <li data-testid="subcategory-breadcrumb">
          <a href="/product_listing?subcategory={subcategoryId}">
            {subcategory.name}
          </a>
        </li>
      {/if}
    </ol>
  </div>

  {#if showLargeTextFinalBreadcrumb}
    <h1
      data-testid="large-breadcrumb-text"
      class:fizz-heading-3={$LayoutStore.isMobile}
      class:fizz-heading-2={$LayoutStore.isDesktop}
    >
      {largeBreadcrumbText}
    </h1>
  {/if}
</div>
